/**
 * TimbricaPlayer — modular video player for Timbrica tools.
 *
 * Usage:
 *   const player = new TimbricaPlayer(containerEl, {
 *       trim: true,             // show trim handles
 *       speed: true,            // show speed selector
 *       volume: true,           // show volume control (default true)
 *       fullscreen: true,       // show fullscreen button (default true)
 *       fsPreferWrap: false,    // fullscreen the wrap before the <video> —
 *                               // keeps tool overlays (subtitles, control
 *                               // pills appended into player.container)
 *                               // visible in FS; default order unchanged
 *       skipSeconds: 0,         // >0 → show ±N s skip buttons
 *       pip: false,             // show Picture-in-Picture button (if supported)
 *       maxHeight: 400,         // max video height: px, or any CSS length ('min(640px, calc(100svh - 150px))')
 *       onReady: fn(player),    // metadata loaded
 *       onTimeUpdate: fn(t),    // current time changed
 *       onTrimChange: fn(s,e),  // trim range changed
 *       onEnded: fn(),          // playback ended
 *   });
 *   player.load(urlOrFile);
 *   player.play(); player.pause(); player.togglePlay();
 *   player.seek(seconds);
 *   player.setSpeed(1.5);
 *   player.setVolume(0.8);
 *   player.setTrimRange(2, 10);
 *   player.getTrimRange(); // {start, end}
 *   player.destroy();
 *   player.video // raw <video> element for advanced use
 */
(function(){
'use strict';

/* ═══════════════════════════════════════════
   CSS — injected once on first instantiation
   ═══════════════════════════════════════════ */
var cssInjected = false;
var CSS = `
/* TimbricaPlayer */
.tp-wrap{position:relative;border-radius:12px;overflow:hidden;background:#000;line-height:0;user-select:none;-webkit-user-select:none}
.tp-wrap *{box-sizing:border-box}
.tp-video{width:100%;display:block;background:#000;cursor:pointer}
.tp-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:2;transition:opacity .2s}
.tp-overlay.playing{opacity:0;pointer-events:none}
.tp-play-icon{width:60px;height:60px;background:rgba(0,0,0,.55);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border-radius:50%;display:flex;align-items:center;justify-content:center;transition:transform .15s}
.tp-play-icon:hover{transform:scale(1.08)}
.tp-play-icon svg{width:28px;height:28px;fill:#fff;margin-left:3px}

/* Controls bar */
.tp-controls{position:absolute;bottom:0;left:0;right:0;display:flex;align-items:center;gap:6px;padding:8px 12px;background:linear-gradient(transparent,rgba(0,0,0,.7) 40%);z-index:5;opacity:0;transition:opacity .25s;line-height:1}
.tp-wrap:hover .tp-controls,.tp-wrap.tp-show-controls .tp-controls,.tp-controls:focus-within{opacity:1}
.tp-wrap.paused .tp-controls{opacity:1}

/* Buttons */
.tp-btn{background:none;border:none;color:#fff;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background .15s;flex-shrink:0}
.tp-btn:hover{background:rgba(255,255,255,.15)}
.tp-btn svg{width:18px;height:18px;fill:currentColor}

/* Time */
.tp-time{font-size:12px;color:rgba(255,255,255,.85);white-space:nowrap;font-variant-numeric:tabular-nums;font-family:system-ui,sans-serif;flex-shrink:0;padding:0 2px}

/* Seekbar */
.tp-seekbar{flex:1;position:relative;height:18px;cursor:pointer;display:flex;align-items:center;min-width:40px}
.tp-seekbar-track{position:absolute;left:0;right:0;height:4px;background:rgba(255,255,255,.2);border-radius:2px;top:50%;transform:translateY(-50%)}
.tp-seekbar-buffered{position:absolute;height:4px;background:rgba(255,255,255,.15);border-radius:2px;top:50%;transform:translateY(-50%);left:0;width:0}
.tp-seekbar-progress{position:absolute;height:4px;background:var(--accent,#38bdf8);border-radius:2px;top:50%;transform:translateY(-50%);left:0;width:0}
.tp-seekbar-head{position:absolute;width:14px;height:14px;background:#fff;border-radius:50%;top:50%;transform:translate(-50%,-50%);box-shadow:0 1px 4px rgba(0,0,0,.4);z-index:3;opacity:0;transition:opacity .15s}
.tp-seekbar:hover .tp-seekbar-head,.tp-seekbar.dragging .tp-seekbar-head{opacity:1}
.tp-seekbar:hover .tp-seekbar-track{height:6px}
.tp-seekbar.dragging .tp-seekbar-track{height:6px}

/* Trim handles */
.tp-trim-region{position:absolute;top:50%;height:14px;transform:translateY(-50%);background:rgba(56,189,248,.15);border-top:2px solid var(--accent,#38bdf8);border-bottom:2px solid var(--accent,#38bdf8);z-index:1;pointer-events:none}
.tp-trim-handle{position:absolute;top:50%;width:10px;height:22px;background:var(--accent,#38bdf8);border-radius:3px;transform:translate(-50%,-50%);cursor:ew-resize;z-index:4;display:flex;align-items:center;justify-content:center}
.tp-trim-handle::after{content:'';width:2px;height:10px;background:rgba(255,255,255,.6);border-radius:1px}
.tp-trim-handle:hover,.tp-trim-handle.dragging{background:#fff;box-shadow:0 0 6px rgba(56,189,248,.5)}
.tp-trim-dimmed{position:absolute;top:50%;height:14px;transform:translateY(-50%);background:rgba(0,0,0,.4);z-index:1;pointer-events:none}

/* Volume — YouTube-style vertical slider that pops up above the speaker button.
   Shown on hover of the wrapper (desktop) or tap of the button (mobile/touch).
   Button click remains a pure mute toggle, independent of the slider. */
.tp-vol-wrap{position:relative;flex-shrink:0}
.tp-vol-panel{position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%) scale(.92);width:36px;height:108px;background:rgba(20,20,24,.92);backdrop-filter:blur(10px) saturate(160%);-webkit-backdrop-filter:blur(10px) saturate(160%);border:1px solid rgba(255,255,255,.08);border-radius:10px;padding:12px 0 10px;display:flex;flex-direction:column;align-items:center;gap:6px;opacity:0;pointer-events:none;transform-origin:50% 100%;transition:opacity .15s,transform .15s;z-index:10;box-shadow:0 4px 16px rgba(0,0,0,.35)}
/* Invisible hover-bridge across the 6px gap between volume button and panel.
   Without it, cursor moving from button toward the slider falls into a dead
   zone and panel disappears before the cursor reaches the track. */
.tp-vol-panel::before{content:'';position:absolute;left:-4px;right:-4px;top:100%;height:10px}
.tp-vol-wrap:hover .tp-vol-panel,.tp-vol-panel:hover,.tp-vol-wrap.tp-vol-open .tp-vol-panel,.tp-vol-wrap:focus-within .tp-vol-panel{opacity:1;pointer-events:auto;transform:translateX(-50%) scale(1)}
.tp-vol-track{position:relative;width:4px;flex:1;background:rgba(255,255,255,.2);border-radius:2px;cursor:pointer;touch-action:none}
.tp-vol-fill{position:absolute;left:0;right:0;bottom:0;background:var(--accent,#38bdf8);border-radius:2px;pointer-events:none}
.tp-vol-thumb{position:absolute;left:50%;width:12px;height:12px;background:#fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,.4);transform:translate(-50%,50%);pointer-events:none;transition:transform .1s}
.tp-vol-track:hover .tp-vol-thumb,.tp-vol-track.dragging .tp-vol-thumb{transform:translate(-50%,50%) scale(1.25)}
.tp-vol-pct{font-size:12px;color:rgba(255,255,255,.75);font-family:system-ui,sans-serif;font-variant-numeric:tabular-nums;line-height:1;min-height:12px;letter-spacing:.02em}

/* Speed */
.tp-speed-btn{font-size:12px;font-weight:700;min-width:32px;text-align:center;color:rgba(255,255,255,.85);font-family:system-ui,sans-serif}

/* Fullscreen */
.tp-wrap.tp-fs{position:fixed!important;inset:0!important;z-index:99999!important;border-radius:0!important;max-height:none!important}
.tp-wrap.tp-fs .tp-video{max-height:100vh!important;height:100vh;object-fit:contain}

/* Tooltip */
.tp-tooltip{position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);background:rgba(0,0,0,.8);color:#fff;font-size:12px;padding:2px 6px;border-radius:4px;white-space:nowrap;pointer-events:none;opacity:0;transition:opacity .15s;font-family:system-ui,sans-serif;z-index:10}
/* hover only where there IS hover: on a phone :hover sticks after a tap and
   would leave the bubble (now a picture) parked over the video */
@media(hover:hover){.tp-seekbar:hover .tp-tooltip{opacity:1}}
.tp-seekbar.dragging .tp-tooltip{opacity:1}
/* Frame preview in the bubble (setSeekPreview). The picture sits above the
   time; the bubble is clamped to the player so the picture never hangs off
   its edge at 0:00 or at the end. */
.tp-tooltip.tp-has-thumb{padding:3px 3px 2px;border-radius:8px;background:rgba(10,10,12,.92);box-shadow:0 4px 18px rgba(0,0,0,.45)}
.tp-thumb{display:none;border-radius:6px;background:#000}
.tp-tooltip.tp-has-thumb .tp-thumb{display:block}
.tp-tip-txt{display:block;text-align:center;line-height:1.35}
.tp-tooltip.tp-has-thumb .tp-tip-txt{padding-top:2px}

/* Mobile — hide vertical panel on narrow screens; OS volume is the expected UX.
   The mute button stays as a one-tap way to silence audio. */
@media(max-width:640px){
    .tp-controls{padding:6px 8px;gap:4px}
    .tp-time{font-size:12px}
    .tp-vol-panel{display:none}
    .tp-play-icon{width:50px;height:50px}
    .tp-play-icon svg{width:24px;height:24px}
}
@media(pointer:coarse){
    .tp-seekbar-head{opacity:1;width:16px;height:16px}
    .tp-trim-handle{width:14px;height:28px}
    .tp-seekbar{height:24px}
}
`;

function injectCSS(){
    if(cssInjected) return;
    var s = document.createElement('style');
    s.textContent = CSS;
    document.head.appendChild(s);
    cssInjected = true;
}

/* ═══════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════ */
function fmtTime(s){
    if(!isFinite(s)||s<0) s=0;
    var h=Math.floor(s/3600), m=Math.floor((s%3600)/60), sec=Math.floor(s%60);
    if(h>0) return h+':'+String(m).padStart(2,'0')+':'+String(sec).padStart(2,'0');
    return m+':'+String(sec).padStart(2,'0');
}

function clamp(v,min,max){return Math.max(min,Math.min(max,v));}

function svg(paths, w, h){
    return '<svg viewBox="0 0 '+(w||24)+' '+(h||24)+'" xmlns="http://www.w3.org/2000/svg">'+paths+'</svg>';
}

var ICON_PLAY = svg('<path d="M8 5v14l11-7z"/>');
var ICON_PAUSE = svg('<path d="M6 4h4v16H6zm8 0h4v16h-4z"/>');
var ICON_VOL_ON = svg('<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 8.5v7a4.47 4.47 0 002.5-3.5zM14 3.23v2.06a6.97 6.97 0 010 13.42v2.06A9 9 0 0014 3.23z"/>');
var ICON_VOL_OFF = svg('<path d="M16.5 12A4.5 4.5 0 0014 8.5v2.09l2.4 2.4c.07-.3.1-.6.1-.99zm2.5 0a6.86 6.86 0 01-.54 2.7l1.53 1.53A8.94 8.94 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06A6.97 6.97 0 0119 12zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.9 8.9 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>');
var ICON_FS_ENTER = svg('<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>');
var ICON_FS_EXIT = svg('<path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>');
var ICON_SKIP_BACK = svg('<path d="M12 5V1L7 6l5 5V7a6 6 0 11-6 6H4a8 8 0 108-8z"/>');
var ICON_SKIP_FWD = svg('<path d="M12 5V1l5 5-5 5V7a6 6 0 106 6h2a8 8 0 11-8-8z"/>');
var ICON_PIP = svg('<path d="M19 7h-8v6h8V7zm4-4H1v18h22V3zm-2 16H3V5h18v14z"/>');

/* ═══════════════════════════════════════════
   TimbricaPlayer Class
   ═══════════════════════════════════════════ */
/* ═══════════════════════════════════════════
   Volume knob — the vertical popup on its own
   ═══════════════════════════════════════════
   The player already had the control people expect from a player: a speaker
   button with a VERTICAL slider that appears on hover (desktop) or tap
   (touch), and disappears again. Every other play row on the site had a
   horizontal `input[type=range]` sitting open next to the button — 96 tool
   blades carry one — which is both a different look and, in a row that also
   holds a waveform, the thing that makes the row overflow.

   This is that control, liftable. It owns a value and a mute flag and reports
   them; it does not know what it is turning down, so a <video>, a GainNode or
   a plain number all work. TimbricaPlayer builds its own volume through this
   function, which is what keeps the two from drifting — there is one
   implementation and one stylesheet, not a copy per caller.

   ⚠️ The document-level touch listener that closes the panel is removed by
   destroy(). The player never removed it because a player lives as long as its
   page; a tool that mounts and unmounts a row (a2v resets on «Другой файл»)
   would otherwise leak one listener per mount. */
function createVolumeKnob(opts){
    opts = opts || {};
    injectCSS();

    var value = (typeof opts.value === 'number') ? clamp(opts.value, 0, 1) : 1;
    var muted = !!opts.muted;
    /* Where unmuting returns to. A slider dragged to zero and then un-muted
       must not stay silent, which is why this is remembered separately. */
    var restore = value > 0 ? value : 0.5;
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function(){};
    var label = opts.label || 'Volume';

    var wrap = document.createElement('div'); wrap.className = 'tp-vol-wrap';
    var btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'tp-btn tp-vol-btn';
    btn.innerHTML = ICON_VOL_ON;
    btn.setAttribute('aria-label', label);

    var panel = document.createElement('div'); panel.className = 'tp-vol-panel';
    var track = document.createElement('div'); track.className = 'tp-vol-track';
    track.setAttribute('role', 'slider');
    track.setAttribute('aria-orientation', 'vertical');
    track.setAttribute('aria-label', label);
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    track.tabIndex = 0;
    var fill = document.createElement('div'); fill.className = 'tp-vol-fill';
    var thumb = document.createElement('div'); thumb.className = 'tp-vol-thumb';
    track.appendChild(fill); track.appendChild(thumb);
    var pct = document.createElement('span'); pct.className = 'tp-vol-pct';
    panel.appendChild(track); panel.appendChild(pct);
    wrap.appendChild(btn); wrap.appendChild(panel);

    function paint(){
        var v = muted ? 0 : value;
        var p = Math.round(v * 100);
        fill.style.height = p + '%';
        thumb.style.bottom = p + '%';
        pct.textContent = p;
        track.setAttribute('aria-valuenow', String(p));
        btn.innerHTML = (muted || v === 0) ? ICON_VOL_OFF : ICON_VOL_ON;
    }
    function commit(){ paint(); onChange(muted ? 0 : value, muted, value); }
    function setValue(v, unmute){
        value = clamp(v, 0, 1);
        if(value > 0){ restore = value; if(unmute !== false) muted = false; }
        commit();
    }
    function fromEvent(e){
        var r = track.getBoundingClientRect();
        var cy = e.touches ? e.touches[0].clientY : e.clientY;
        setValue(1 - (cy - r.top) / r.height);   // vertical: bottom 0, top 1
    }

    var dragging = false;
    function move(e){ if(dragging){ if(e.preventDefault) e.preventDefault(); fromEvent(e); } }
    function end(){
        dragging = false; track.classList.remove('dragging');
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', end);
        document.removeEventListener('touchmove', move);
        document.removeEventListener('touchend', end);
    }
    function start(e){
        e.preventDefault();
        dragging = true; track.classList.add('dragging');
        fromEvent(e);
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', end);
        document.addEventListener('touchmove', move, {passive:false});
        document.addEventListener('touchend', end);
    }
    track.addEventListener('mousedown', start);
    track.addEventListener('touchstart', start, {passive:false});

    var onWheel = function(e){
        e.preventDefault();
        setValue(value + (e.deltaY > 0 ? -0.05 : 0.05));
    };
    wrap.addEventListener('wheel', onWheel, {passive:false});

    /* Speaker click = mute toggle only. stopPropagation so it does not bubble
       to the wrap and reopen the panel the tap is trying to close. */
    /* One tap, one action. On a touch screen that shows the panel (tablets), the tap that OPENS it only
       opens it; with the panel open, a tap on the speaker mutes; a tap outside closes it. Until
       29.09.2026 touchstart toggled the panel AND the click of the same tap toggled mute — opening the
       slider always muted the video. Phones hide the panel (≤640 px): there the tap is the mute toggle,
       as before. A keyboard click (detail 0) always mutes. */
    var tapOpenedAt = -1e9;
    btn.addEventListener('click', function(e){
        e.stopPropagation();
        if(e.detail !== 0 && Date.now() - tapOpenedAt < 800){ tapOpenedAt = -1e9; return; }
        if(muted || value === 0){ muted = false; if(value === 0) value = restore || 0.5; }
        else muted = true;
        commit();
    });
    var onBtnTouch = function(){
        if(getComputedStyle(panel).display === 'none') return;          // phones: no panel, the tap mutes
        if(!wrap.classList.contains('tp-vol-open')){ wrap.classList.add('tp-vol-open'); tapOpenedAt = Date.now(); }
    };
    btn.addEventListener('touchstart', onBtnTouch, {passive:true});
    var onDocTouch = function(e){ if(!wrap.contains(e.target)) wrap.classList.remove('tp-vol-open'); };
    document.addEventListener('touchstart', onDocTouch, {passive:true});

    track.addEventListener('keydown', function(e){
        var step = e.shiftKey ? 0.1 : 0.05;
        if(e.key === 'ArrowUp' || e.key === 'ArrowRight'){ e.preventDefault(); setValue(value + step); }
        else if(e.key === 'ArrowDown' || e.key === 'ArrowLeft'){ e.preventDefault(); setValue(value - step, false); }
        else if(e.key === 'Home'){ e.preventDefault(); setValue(0, false); }
        else if(e.key === 'End'){ e.preventDefault(); setValue(1); }
    });

    paint();
    return {
        el: wrap,
        button: btn,
        /* Reflect state that changed elsewhere. Deliberately does NOT call
           onChange — a player feeding this from its own `volumechange` event
           would otherwise write back what it just read, forever. */
        set: function(v, m){
            if(typeof v === 'number') { value = clamp(v, 0, 1); if(value > 0) restore = value; }
            if(typeof m === 'boolean') muted = m;
            paint();
        },
        get: function(){ return { value: value, muted: muted }; },
        destroy: function(){
            end();
            wrap.removeEventListener('wheel', onWheel);
            btn.removeEventListener('touchstart', onBtnTouch);
            document.removeEventListener('touchstart', onDocTouch);
            if(wrap.parentNode) wrap.parentNode.removeChild(wrap);
        }
    };
}

function TimbricaPlayer(container, opts){
    if(!container) throw new Error('TimbricaPlayer: container required');
    injectCSS();

    opts = opts || {};
    var self = this;
    var showTrim = !!opts.trim;
    var showSpeed = opts.speed !== false && opts.speed;
    var showVolume = opts.volume !== false;
    var showFullscreen = opts.fullscreen !== false;
    var maxH = opts.maxHeight || 400;

    /* State */
    var dur = 0;
    var trimS = 0, trimE = 0;
    var isDraggingSeek = false;
    var isDraggingTrimS = false, isDraggingTrimE = false;
    var currentSpeed = 1;
    var isFS = false;
    var controlsTimer = null;

    /* Build DOM */
    var wrap = document.createElement('div');
    wrap.className = 'tp-wrap paused';

    var video = document.createElement('video');
    video.className = 'tp-video';
    video.playsInline = true;
    video.preload = 'metadata';
    // A number is pixels; a string is a CSS length, so a page can tie the
    // box to the SCREEN (svh) instead of guessing one height for every device.
    // A browser without the unit (svh: Chrome <108, Safari <15.4) ignores the
    // assignment and leaves the property empty — then the old pixel default.
    if (typeof maxH === 'number') video.style.maxHeight = maxH + 'px';
    else { video.style.maxHeight = String(maxH); if (!video.style.maxHeight) video.style.maxHeight = '400px'; }
    wrap.appendChild(video);
    self.video = video;

    /* Overlay */
    var overlay = document.createElement('div');
    overlay.className = 'tp-overlay';
    overlay.innerHTML = '<div class="tp-play-icon">' + ICON_PLAY + '</div>';
    wrap.appendChild(overlay);

    /* Controls */
    var controls = document.createElement('div');
    controls.className = 'tp-controls';

    // Skip back (optional, before play)
    var skipSecs = +opts.skipSeconds || 0;
    var skipBackBtn = null, skipFwdBtn = null;
    if(skipSecs > 0){
        skipBackBtn = mkBtn(ICON_SKIP_BACK, 'tp-skip-btn');
        skipBackBtn.setAttribute('aria-label', '-' + skipSecs + 's');
        controls.appendChild(skipBackBtn);
    }

    // Play button
    var playBtn = mkBtn(ICON_PLAY, 'tp-play-btn');
    controls.appendChild(playBtn);

    if(skipSecs > 0){
        skipFwdBtn = mkBtn(ICON_SKIP_FWD, 'tp-skip-btn');
        skipFwdBtn.setAttribute('aria-label', '+' + skipSecs + 's');
        controls.appendChild(skipFwdBtn);
    }

    // Time
    var timeEl = document.createElement('span');
    timeEl.className = 'tp-time';
    timeEl.textContent = '0:00 / 0:00';
    controls.appendChild(timeEl);

    // Seekbar
    var seekbar = document.createElement('div');
    seekbar.className = 'tp-seekbar';
    var seekTrack = document.createElement('div'); seekTrack.className = 'tp-seekbar-track';
    var seekBuf = document.createElement('div'); seekBuf.className = 'tp-seekbar-buffered';
    var seekProg = document.createElement('div'); seekProg.className = 'tp-seekbar-progress';
    var seekHead = document.createElement('div'); seekHead.className = 'tp-seekbar-head';
    var seekTooltip = document.createElement('div'); seekTooltip.className = 'tp-tooltip';
    var seekThumb = document.createElement('canvas'); seekThumb.className = 'tp-thumb';
    var seekTipTxt = document.createElement('span'); seekTipTxt.className = 'tp-tip-txt';
    seekTooltip.appendChild(seekThumb); seekTooltip.appendChild(seekTipTxt);
    seekbar.appendChild(seekTrack);
    seekbar.appendChild(seekBuf);

    // Trim elements (before progress so they render under)
    var trimDimL, trimDimR, trimRegion, trimHandleS, trimHandleE;
    if(showTrim){
        trimDimL = document.createElement('div'); trimDimL.className = 'tp-trim-dimmed'; trimDimL.style.left = '0';
        trimDimR = document.createElement('div'); trimDimR.className = 'tp-trim-dimmed'; trimDimR.style.right = '0';
        trimRegion = document.createElement('div'); trimRegion.className = 'tp-trim-region';
        trimHandleS = document.createElement('div'); trimHandleS.className = 'tp-trim-handle'; trimHandleS.title = ((window.__commonStrings||{}).js_trim_start || "Trim start");
        trimHandleE = document.createElement('div'); trimHandleE.className = 'tp-trim-handle'; trimHandleE.title = ((window.__commonStrings||{}).js_trim_end || "Trim end");
        seekbar.appendChild(trimDimL);
        seekbar.appendChild(trimDimR);
        seekbar.appendChild(trimRegion);
        seekbar.appendChild(trimHandleS);
        seekbar.appendChild(trimHandleE);
    }

    seekbar.appendChild(seekProg);
    seekbar.appendChild(seekHead);
    seekbar.appendChild(seekTooltip);
    controls.appendChild(seekbar);

    // Volume — vertical pop-up panel (YouTube Music / YouTube style). Custom
    // div-based slider instead of <input type=range> because vertical range
    // inputs require `writing-mode:bt-lr` hacks that render inconsistently
    // across Chrome/Safari/Firefox thumb sizing.
    /* The knob is the shared control (createVolumeKnob above): the player is
       just its first caller, which is what stops the two from drifting. */
    var volKnob = null;
    var currentVolume = 1;
    if(showVolume){
        volKnob = createVolumeKnob({
            value: 1, muted: false, label: 'Volume',
            onChange: function(effective, isMuted, raw){
                video.muted = isMuted;
                video.volume = raw;
                if(raw > 0 && !isMuted) currentVolume = raw;
            }
        });
        controls.appendChild(volKnob.el);
    }

    // Speed
    var speedBtn;
    if(showSpeed){
        speedBtn = document.createElement('button');
        speedBtn.className = 'tp-btn tp-speed-btn';
        speedBtn.textContent = '1x';
        controls.appendChild(speedBtn);
    }

    // Picture-in-Picture (optional; only when the platform supports it)
    var pipBtn = null;
    if(opts.pip && document.pictureInPictureEnabled && video.requestPictureInPicture){
        pipBtn = mkBtn(ICON_PIP, 'tp-pip-btn');
        pipBtn.setAttribute('aria-label', 'Picture-in-Picture');
        controls.appendChild(pipBtn);
    }

    // Fullscreen
    var fsBtn;
    if(showFullscreen){
        fsBtn = mkBtn(ICON_FS_ENTER, 'tp-fs-btn');
        controls.appendChild(fsBtn);
    }

    wrap.appendChild(controls);
    container.innerHTML = '';
    container.appendChild(wrap);

    /* ═══ Events ═══ */

    // Play/pause
    overlay.addEventListener('click', function(){ self.togglePlay(); });
    playBtn.addEventListener('click', function(){ self.togglePlay(); });

    // Click on the video surface toggles play/pause (YouTube-style). While
    // paused the overlay (inset:0) owns the click; while playing it goes
    // pointer-events:none, so clicks land on the <video> itself. On touch,
    // a tap while the controls are hidden only reveals them (first tap =
    // show controls, second tap = pause) — pointerdown fires BEFORE the
    // tap's synthetic mousemove adds tp-show-controls, so it still sees the
    // pre-tap state. Browsers without PointerEvent just always toggle.
    var tapRevealsControls = false;
    video.addEventListener('pointerdown', function(e){
        tapRevealsControls = e.pointerType === 'touch' && !video.paused &&
            !wrap.classList.contains('tp-show-controls');
    });
    video.addEventListener('click', function(){
        if(tapRevealsControls){ tapRevealsControls = false; return; }
        self.togglePlay();
    });

    if(skipBackBtn) skipBackBtn.addEventListener('click', function(){ self.skip(-skipSecs); });
    if(skipFwdBtn) skipFwdBtn.addEventListener('click', function(){ self.skip(skipSecs); });
    if(pipBtn) pipBtn.addEventListener('click', function(){
        try {
            if(document.pictureInPictureElement) document.exitPictureInPicture().catch(function(){});
            else video.requestPictureInPicture().catch(function(){});
        } catch(e){ /* PiP denial must never surface as an exception */ }
    });

    video.addEventListener('play', function(){
        wrap.classList.remove('paused');
        showControlsTemporarily();   // show now, auto-hide in 2.5s (mobile has no mousemove to re-arm)
        overlay.classList.add('playing');
        playBtn.innerHTML = ICON_PAUSE;
    });
    video.addEventListener('pause', function(){
        wrap.classList.add('paused');
        overlay.classList.remove('playing');
        overlay.querySelector('.tp-play-icon').innerHTML = ICON_PLAY;
        playBtn.innerHTML = ICON_PLAY;
    });
    video.addEventListener('ended', function(){
        if(opts.onEnded) opts.onEnded();
    });

    // Metadata
    video.addEventListener('loadedmetadata', function(){
        dur = video.duration;
        trimS = 0; trimE = dur;
        updateTime();
        updateTrimUI();
        if(opts.onReady) opts.onReady(self);
    });

    // Decode/source error. The <video> can't play this codec (HEVC/H.265 from
    // iPhones, 10-bit, ProRes, AV1 on older engines) or the source is corrupt:
    // the browser fires 'error', 'loadedmetadata' never comes, so onReady never
    // fires. Without a listener the host tool just shows a dead black box — the
    // literal "video doesn't load" complaint. Surface it so callers can degrade
    // gracefully (FFmpeg WASM still decodes most of these, so audio
    // extraction / captioning can proceed even when in-browser preview can't).
    // Guarded so the teardown in destroy() (removeAttribute+load) stays silent.
    video.addEventListener('error', function(){
        if(self._destroyed) return;
        var err = video.error;
        if(!err) return;
        // code 1 = MEDIA_ERR_ABORTED: load() was interrupted (rapid re-source /
        // teardown), NOT a decode failure — ignore. 2/3/4 = network / decode /
        // src-not-supported = the real "can't play this" cases. (A fatal error
        // can blank video.currentSrc, so don't gate on it.)
        if(err.code === 1) return;
        if(opts.onError){ try { opts.onError(err, self); } catch(e){} return; }
        // No tool-specific handler → SHARED DEFAULT: an honest, localized note
        // instead of a dead black box, covering every tool that previews a raw
        // upload through this player at once. Self-selecting — it can only fire
        // on a REAL decode failure (an undecodable user upload), never on a
        // tool-GENERATED or browser-RECORDED source (those decode fine, so the
        // 'error' event never fires). Tools wanting a tailored message
        // (auto-captions "captions still work") pass opts.onError to override.
        try {
            if(!self._previewErrShown){
                self._previewErrShown = true;   // once per player instance
                var L = ((typeof window!=='undefined' && window.__LOCALE) || 'en').slice(0,2);
                var M = {
                    en:'This video can’t preview in your browser, but the tool will still process it.',
                    ru:'Это видео не показывается в вашем браузере, но инструмент всё равно его обработает.',
                    ko:'이 동영상은 브라우저에서 미리 볼 수 없지만 도구는 정상적으로 처리합니다.',
                    ar:'لا يمكن معاينة هذا الفيديو في متصفحك، لكن الأداة ستعالجه على أي حال.',
                    id:'Video ini tidak bisa dipratinjau di browser Anda, tetapi alat tetap akan memprosesnya.',
                    pt:'Este vídeo não pode ser pré-visualizado no seu navegador, mas a ferramenta ainda o processará.',
                    de:'Dieses Video kann in Ihrem Browser nicht angezeigt werden, aber das Tool verarbeitet es trotzdem.',
                    es:'Este vídeo no se puede previsualizar en tu navegador, pero la herramienta lo procesará igualmente.',
                    fr:'Cette vidéo ne peut pas s’afficher dans votre navigateur, mais l’outil la traitera quand même.',uk:'Це відео не відтворюється у вашому браузері, але інструмент усе одно його обробить.'
                };
                if(typeof window!=='undefined' && typeof window.__toast==='function') window.__toast(M[L]||M.en, 'warning');
            }
            if(typeof window!=='undefined' && typeof window.__aeFeature==='function'){
                // role/rs/ns (2026-07-02): the bare {code} payload couldn't
                // distinguish WHICH player failed (source preview vs a tool's
                // result player) nor at what load state — the mute-video
                // result-era investigation stalled on exactly that gap
                // (windows/yandex cohort: error fires minutes after complete,
                // unreproducible in vanilla Blink). role comes from
                // opts.telemetryRole (tools label their players); omitted
                // when unset so old rows stay comparable.
                var _pv = { code: err.code, src: 'shared-player', rs: video.readyState, ns: video.networkState };
                if(opts.telemetryRole) _pv.role = opts.telemetryRole;
                window.__aeFeature('preview_unavailable', _pv);
            }
        } catch(e){}
    });

    // Time update
    video.addEventListener('timeupdate', function(){
        if(isDraggingSeek) return;
        updateTime();
        // Enforce trim bounds during playback
        if(showTrim && !video.paused){
            if(video.currentTime >= trimE - 0.05){
                video.pause();
                video.currentTime = trimE;
            }
        }
        if(opts.onTimeUpdate) opts.onTimeUpdate(video.currentTime);
    });

    // Buffered
    video.addEventListener('progress', function(){
        if(dur > 0 && video.buffered.length > 0){
            var end = video.buffered.end(video.buffered.length - 1);
            seekBuf.style.width = (end / dur * 100) + '%';
        }
    });

    // Seekbar — click & drag
    seekbar.addEventListener('mousedown', onSeekStart);
    seekbar.addEventListener('touchstart', onSeekStart, {passive:false});

    function onSeekStart(e){
        // Check if we hit a trim handle first
        if(showTrim){
            var t = e.target;
            if(t === trimHandleS || t.closest('.tp-trim-handle') === trimHandleS){
                startTrimDrag('start', e); return;
            }
            if(t === trimHandleE || t.closest('.tp-trim-handle') === trimHandleE){
                startTrimDrag('end', e); return;
            }
        }
        isDraggingSeek = true;
        seekbar.classList.add('dragging');
        seekTo(e);
        document.addEventListener('mousemove', onSeekMove);
        document.addEventListener('mouseup', onSeekEnd);
        document.addEventListener('touchmove', onSeekMove, {passive:false});
        document.addEventListener('touchend', onSeekEnd);
    }
    function onSeekMove(e){ if(isDraggingSeek) seekTo(e); }
    function onSeekEnd(){
        isDraggingSeek = false;
        seekbar.classList.remove('dragging');
        document.removeEventListener('mousemove', onSeekMove);
        document.removeEventListener('mouseup', onSeekEnd);
        document.removeEventListener('touchmove', onSeekMove);
        document.removeEventListener('touchend', onSeekEnd);
    }

    function seekTo(e){
        if(!dur) return;
        var r = seekbar.getBoundingClientRect();
        var cx = (e.touches ? e.touches[0].clientX : e.clientX);
        var pct = clamp((cx - r.left) / r.width, 0, 1);
        var t = pct * dur;
        if(showTrim) t = clamp(t, trimS, trimE);
        if(!isFinite(t)) return;
        video.currentTime = t;
        updateTime();
        tipAt(cx);
    }

    // Seekbar tooltip. seekLabeler lets the owning tool add context the player
    // cannot know — chapter titles, for one: a bare timecode says "01:14:22"
    // where the viewer wants "The rooftop chase".
    var seekLabeler = null;
    /* Frame preview: a provider the owning tool supplies —
         { peek(t) → {canvas}|null  (cached, synchronous)
           want(t, {exact}) → Promise<{canvas}|null> }
       The player only paints; which frame, and whether an exact one is worth
       decoding right now, is the provider's call (seek-preview.js). */
    var seekPreview = null, tipSeq = 0, tipT = -1, dwellTimer = null;
    function paintThumb(res, seq){
        if (!res || !res.canvas || seq !== tipSeq) return;
        var c = res.canvas, dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
        if (seekThumb.width !== c.width || seekThumb.height !== c.height){ seekThumb.width = c.width; seekThumb.height = c.height; }
        seekThumb.style.width = Math.round(c.width / dpr) + 'px';
        seekThumb.style.height = Math.round(c.height / dpr) + 'px';
        try { seekThumb.getContext('2d').drawImage(c, 0, 0); } catch(_){ return; }
        if (!seekTooltip.classList.contains('tp-has-thumb')){ seekTooltip.classList.add('tp-has-thumb'); placeTip(); }
    }
    // a failed frame must not leave the PREVIOUS picture standing for this time
    function dropThumb(seq){ if (seq === tipSeq) seekTooltip.classList.remove('tp-has-thumb'); }
    var tipPct = 0;
    function placeTip(){
        // centred on the pointer, but kept inside the player: the seek bar is
        // narrower than the wrap, so the limits are the WRAP's edges
        var sb = seekbar.getBoundingClientRect(), wr = wrap.getBoundingClientRect();
        var half = seekTooltip.offsetWidth / 2, x = tipPct * sb.width;
        var minX = (wr.left + 4 + half) - sb.left, maxX = (wr.right - 4 - half) - sb.left;
        if (maxX > minX) x = clamp(x, minX, maxX);
        seekTooltip.style.left = x + 'px';
    }
    function tipAt(clientX){
        if(!dur) return;
        var r = seekbar.getBoundingClientRect();
        var pct = clamp((clientX - r.left) / r.width, 0, 1);
        var t = pct * dur;
        var label = fmtTime(t);
        if (seekLabeler) {
            try {
                var extra = seekLabeler(t);
                if (extra) label += ' · ' + extra;
            } catch(_){}
        }
        seekTipTxt.textContent = label;
        tipPct = pct;
        placeTip();
        if (!seekPreview || Math.abs(t - tipT) < 0.02) return;
        tipT = t;
        var seq = ++tipSeq;
        clearTimeout(dwellTimer);
        try {
            var hit = seekPreview.peek(t);
            if (hit) paintThumb(hit, seq);
            else seekPreview.want(t, {}).then(function(res){ paintThumb(res, seq); }, function(){ dropThumb(seq); });
            // The pointer rests: the frame EXACTLY under it, when the provider
            // says that is cheap now.
            dwellTimer = setTimeout(function(){
                if (seq !== tipSeq) return;
                seekPreview.want(t, { exact: true }).then(function(res){ paintThumb(res, seq); }, function(){});
            }, 160);
        } catch(_){}
    }
    seekbar.addEventListener('mousemove', function(e){ tipAt(e.clientX); });
    seekbar.addEventListener('mouseleave', function(){ clearTimeout(dwellTimer); tipSeq++; tipT = -1; });
    self.setSeekLabeler = function(fn){ seekLabeler = (typeof fn === 'function') ? fn : null; };
    self.setSeekPreview = function(p){
        seekPreview = (p && typeof p.peek === 'function' && typeof p.want === 'function') ? p : null;
        tipSeq++; tipT = -1;
        if (!seekPreview) seekTooltip.classList.remove('tp-has-thumb');
    };

    // Trim drag
    function startTrimDrag(which, e){
        e.preventDefault(); e.stopPropagation();
        var isStart = which === 'start';
        if(isStart) isDraggingTrimS = true; else isDraggingTrimE = true;
        var handle = isStart ? trimHandleS : trimHandleE;
        handle.classList.add('dragging');

        var onMove = function(ev){
            ev.preventDefault();
            if(!dur) return;
            var r = seekbar.getBoundingClientRect();
            var cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
            var pct = clamp((cx - r.left) / r.width, 0, 1);
            var t = pct * dur;
            if(isStart){
                trimS = clamp(t, 0, trimE - 0.1);
            } else {
                trimE = clamp(t, trimS + 0.1, dur);
            }
            updateTrimUI();
            if(opts.onTrimChange) opts.onTrimChange(trimS, trimE);
        };
        var onUp = function(){
            isDraggingTrimS = false; isDraggingTrimE = false;
            handle.classList.remove('dragging');
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, {passive:false});
        document.addEventListener('touchend', onUp);
    }

    /* Volume behaviour (drag, wheel, mute, keyboard, the popup itself) lives in
       createVolumeKnob. All that is left here is keeping the knob showing what
       the <video> actually is — including changes this player did not make. */
    function paintVolume(){
        if(volKnob) volKnob.set(video.volume, video.muted);
    }
    if(showVolume){
        video.addEventListener('volumechange', paintVolume);
        paintVolume();
    }

    // Speed. preservesPitch keeps voices from turning into chipmunks at 1.5x
    // and into a growl at 0.75x — without it, "watch it a bit faster" is
    // unusable for dialogue, which is the main reason anyone changes speed.
    // Prefixed spellings are for older WebKit/Firefox builds.
    function applyRate(r){
        video.playbackRate = r;
        try {
            video.preservesPitch = true;
            video.mozPreservesPitch = true;
            video.webkitPreservesPitch = true;
        } catch(_){}
    }
    var speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
    if(showSpeed && speedBtn){
        speedBtn.addEventListener('click', function(){
            var idx = speeds.indexOf(currentSpeed);
            idx = (idx + 1) % speeds.length;
            currentSpeed = speeds[idx];
            applyRate(currentSpeed);
            speedBtn.textContent = currentSpeed + 'x';
        });
    }
    // A newly loaded source resets playbackRate/preservesPitch to defaults.
    video.addEventListener('loadedmetadata', function(){ if (currentSpeed !== 1) applyRate(currentSpeed); });

    // Fullscreen — three-tier graceful chain:
    //   1. <video> native FS  — iOS Safari only allows FS on <video>; Yandex
    //      Browser is far more likely to grant FS on <video> than on a div
    //      wrap (regional policies can deny the latter with "not granted").
    //   2. wrap native FS      — desktop Chromium/Firefox prefer this since the
    //      whole player UI (controls, trim) goes fullscreen with the video.
    //   3. CSS pseudo-FS       — `.tp-fs` makes the wrap fixed/inset:0; works
    //      even in iframes / embedded WKWebViews / when both natives reject.
    // Every native call is .catch()ed so a denial never escapes as
    // unhandled rejection (was source of "TypeError: not granted" in Sentry +
    // user-facing toast on Yandex Browser, 2026-05-01).
    var cssEscHandler = null;
    function tryNativeFs(el){
        try {
            if (el.requestFullscreen) {
                var p = el.requestFullscreen();
                return (p && typeof p.then === 'function') ? p : Promise.resolve();
            }
            if (el.webkitRequestFullscreen) { el.webkitRequestFullscreen(); return Promise.resolve(); }
            if (el.webkitEnterFullscreen)   { el.webkitEnterFullscreen();   return Promise.resolve(); }
        } catch (e) { return Promise.reject(e); }
        return null; // no API on this element
    }
    function enterCssFs(){
        wrap.classList.add('tp-fs');
        isFS = true;
        updateFsBtn();
        cssEscHandler = function(e){ if (e.key === 'Escape') exitCssFs(); };
        document.addEventListener('keydown', cssEscHandler, true);
    }
    function exitCssFs(){
        wrap.classList.remove('tp-fs');
        isFS = false;
        updateFsBtn();
        if (cssEscHandler) {
            document.removeEventListener('keydown', cssEscHandler, true);
            cssEscHandler = null;
        }
    }
    /* Set while OUR button is asking for fullscreen, so a fullscreenchange we
       did not ask for is identifiable. Some mobile browsers put video into
       fullscreen on their own — Yandex Browser does it when the phone is
       rotated — and when they do it to the <video> element rather than our
       wrap, every overlay this tool draws (subtitles, chapter marks, the
       stats panel) disappears and the viewer is left with the browser's own
       player. That was the shape of the 2026-08-18 report; whether it is
       common enough to be worth fighting is a question only numbers can
       answer, so: count it, do not guess. */
    var fsAsked = 0;
    function enterFs(){
        fsAsked = Date.now();
        // fsPreferWrap: tools that overlay UI inside the wrap (subtitles,
        // control pills) need the WRAP fullscreened, else their overlays
        // vanish behind the native <video> FS layer. iOS still falls back to
        // <video> (wrap FS unsupported there), which is the platform limit.
        var first = opts.fsPreferWrap ? wrap : video;
        var second = opts.fsPreferWrap ? video : wrap;
        var p1 = tryNativeFs(first);
        if (p1) { p1.then(null, function(){
            var p2 = tryNativeFs(second);
            if (p2) p2.then(null, enterCssFs); else enterCssFs();
        }); return; }
        var p2 = tryNativeFs(second);
        if (p2) p2.then(null, enterCssFs); else enterCssFs();
    }
    function exitFs(){
        // In CSS-mode, native exit APIs are no-ops — handle directly.
        if (cssEscHandler) { exitCssFs(); return; }
        try {
            if (document.exitFullscreen) {
                var p = document.exitFullscreen();
                if (p && typeof p.then === 'function') p.then(null, function(){});
                return;
            }
            if (document.webkitExitFullscreen) { document.webkitExitFullscreen(); return; }
            if (video.webkitExitFullscreen)    { video.webkitExitFullscreen();    return; }
        } catch (e) { /* fall through */ }
        // Nothing worked — sync state to non-FS so UI doesn't get stuck.
        isFS = false;
        wrap.classList.remove('tp-fs');
        updateFsBtn();
    }
    if(showFullscreen && fsBtn){
        fsBtn.addEventListener('click', function(){ if (!isFS) enterFs(); else exitFs(); });
        document.addEventListener('fullscreenchange', onFsChange);
        document.addEventListener('webkitfullscreenchange', onFsChange);
        // iOS Safari fires these on the <video> element (not document) when
        // webkitEnterFullscreen succeeds. Without these listeners the icon
        // would stay stuck on "enter" while the user is actually in FS.
        video.addEventListener('webkitbeginfullscreen', onFsChange);
        video.addEventListener('webkitendfullscreen', onFsChange);
    }
    function onFsChange(){
        var nativeFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
        try {
            var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
            if (fsEl && (fsEl === wrap || fsEl === video) &&
                typeof window !== 'undefined' && typeof window.__aeFeature === 'function') {
                var payload = {
                    target: (fsEl === wrap) ? 'wrap' : 'video',
                    asked: (fsAsked && Date.now() - fsAsked < 4000) ? 1 : 0,
                    /* consumed: one request explains one entry, so a later
                       fullscreen we did not ask for cannot borrow this one's
                       alibi — which is the entire point of the measurement */
                    w: window.innerWidth, h: window.innerHeight
                };
                if (opts.telemetryRole) payload.role = opts.telemetryRole;
                fsAsked = 0;
                window.__aeFeature('fullscreen_entered', payload);
            }
        } catch (_) {}
        // iOS: <video> may be in FS without document.fullscreenElement set.
        var iosVideoFs = !!(video.webkitDisplayingFullscreen);
        // CSS-mode is tracked by cssEscHandler — leave it alone.
        if (cssEscHandler) return;
        isFS = nativeFs || iosVideoFs;
        wrap.classList.toggle('tp-fs', isFS && !iosVideoFs); // iOS owns its own UI
        updateFsBtn();
    }
    function updateFsBtn(){ if(fsBtn) fsBtn.innerHTML = isFS ? ICON_FS_EXIT : ICON_FS_ENTER; }

    // Auto-hide controls. Armed from mousemove AND pointerdown AND play():
    // touch taps don't reliably emit compat mousemove, so before pointerdown
    // was added the bar never auto-hid on mobile once playback started.
    function showControlsTemporarily(){
        wrap.classList.add('tp-show-controls');
        clearTimeout(controlsTimer);
        if(!video.paused){
            controlsTimer = setTimeout(function(){ wrap.classList.remove('tp-show-controls'); }, 2500);
        }
    }
    wrap.addEventListener('mousemove', showControlsTemporarily);
    // Bubbles from the <video>'s own pointerdown (tap-state capture) first —
    // DOM order guarantees the child listener sees the PRE-reveal state.
    wrap.addEventListener('pointerdown', showControlsTemporarily);

    // Keyboard
    wrap.tabIndex = 0;
    wrap.addEventListener('keydown', function(e){
        // Ctrl+F is the browser's find, not fullscreen.
        if(e.ctrlKey || e.metaKey || e.altKey) return;
        // The physical key: on a Russian layout F is «а» and M is «ь», so a
        // character match left fullscreen / mute / K dead there.
        var key = e.key;
        if(/^Key[FKM]$/.test(e.code || '')) key = e.code.charAt(3).toLowerCase();
        switch(key){
            case ' ': case 'k': e.preventDefault(); self.togglePlay(); break;
            case 'ArrowLeft': e.preventDefault(); video.currentTime = Math.max(showTrim?trimS:0, video.currentTime - 5); break;
            case 'ArrowRight': e.preventDefault(); video.currentTime = Math.min(showTrim?trimE:dur, video.currentTime + 5); break;
            case 'ArrowUp': e.preventDefault(); video.volume = clamp(video.volume + 0.1, 0, 1); if(video.volume>0){video.muted=false;currentVolume=video.volume;} if(showVolume) paintVolume(); break;
            case 'ArrowDown': e.preventDefault(); video.volume = clamp(video.volume - 0.1, 0, 1); if(showVolume) paintVolume(); break;
            case 'f': self.toggleFullscreen(); break;
            case 'm': if(showVolume){ video.muted = !video.muted; paintVolume(); } break;
        }
    });

    /* ═══ Internal helpers ═══ */

    function updateTime(){
        if(!dur) return;
        var ct = video.currentTime;
        var pct = ct / dur * 100;
        seekProg.style.width = pct + '%';
        seekHead.style.left = pct + '%';
        if(showTrim){
            timeEl.textContent = fmtTime(ct) + ' / ' + fmtTime(trimE - trimS);
        } else {
            timeEl.textContent = fmtTime(ct) + ' / ' + fmtTime(dur);
        }
    }

    function updateTrimUI(){
        if(!showTrim || !dur) return;
        var pS = trimS / dur * 100, pE = trimE / dur * 100;
        trimDimL.style.width = pS + '%';
        trimDimR.style.width = (100 - pE) + '%';
        trimRegion.style.left = pS + '%';
        trimRegion.style.width = (pE - pS) + '%';
        trimHandleS.style.left = pS + '%';
        trimHandleE.style.left = pE + '%';
        updateTime();
    }

    function mkBtn(iconHtml, extraClass){
        var b = document.createElement('button');
        b.className = 'tp-btn' + (extraClass ? ' ' + extraClass : '');
        b.innerHTML = iconHtml;
        return b;
    }

    /* ═══ Picture watchdog ═══════════════════════════════════════════════
       A mounted player must be able to prove it is showing something.

       The 'error' listener above covers the case where the element SAYS it
       failed. The expensive case is the one where it says nothing: a
       MediaSource that never receives a segment (the converter died), a
       decoder the OS refused, a source that stalls at readyState 0. Measured
       on /video-player: 465 sessions in 30 days sat in front of a black
       rectangle, under a badge claiming playback, with no error event, no
       toast and no way back — because nothing in the stack ever asked
       "is a picture actually there?".

       So ask. `videoWidth > 0 && readyState >= HAVE_CURRENT_DATA` is the
       honest form of that question (readyState alone lies — it reports that
       DATA arrived, not that a frame exists), and requestVideoFrameCallback
       confirms it early when the engine has it.

       The watchdog only REPORTS (opts.onNoPicture + a `tp:nopicture` event on
       the wrap). It draws nothing, so every existing tool behaves exactly as
       before; a tool that wants to say something listens. Silence is opt-out,
       not opt-in, precisely so the next player-mounting tool inherits the
       question instead of having to remember it. */
    /* The deadline counts VISIBLE time (timbrica-core.js __visibleTimeout).
       Chromium does not load a media element in a hidden tab that has not
       played media yet — so a wall-clock watchdog reported «no picture» for
       every source mounted while the viewer was in another tab, and the tool
       in front of it drew a verdict over a file that was fine (/video-player,
       22.09.2026: no_picture {rs:0, ns:2}, the exact fingerprint of a deferred
       load). Without the core the old clock is kept. */
    var cancelPictureTimer = function(){}, sawPicture = false, pictureReported = false;
    function pictureDeadline(fn, ms){
        if(typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
        var h = setTimeout(fn, ms);
        return function(){ clearTimeout(h); };
    }
    var PICTURE_TIMEOUT = (typeof opts.pictureTimeoutMs === 'number') ? opts.pictureTimeoutMs : 15000;

    function hasPicture(){
        return video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2;
    }
    function notePicture(){
        if(sawPicture || !hasPicture()) return;
        sawPicture = true;
        cancelPictureTimer();
        try { wrap.dispatchEvent(new CustomEvent('tp:picture', { bubbles: true })); } catch(_){}
        if(opts.onPicture){ try { opts.onPicture(self); } catch(_){} }
    }
    ['loadedmetadata','loadeddata','canplay','playing','timeupdate','seeked'].forEach(function(ev){
        video.addEventListener(ev, notePicture);
    });
    if(typeof video.requestVideoFrameCallback === 'function'){
        try { video.requestVideoFrameCallback(function(){ notePicture(); }); } catch(_){}
    }
    function armPictureWatchdog(){
        if(PICTURE_TIMEOUT <= 0) return;
        sawPicture = false; pictureReported = false;
        cancelPictureTimer();
        cancelPictureTimer = pictureDeadline(function(){
            cancelPictureTimer = function(){};
            if(self._destroyed || sawPicture || hasPicture()){ notePicture(); return; }
            if(pictureReported) return;
            pictureReported = true;
            var detail = {
                reason: video.error ? 'error' : (video.readyState === 0 ? 'no_data' : 'no_frame'),
                code: video.error ? video.error.code : 0,
                readyState: video.readyState, networkState: video.networkState,
                waitedMs: PICTURE_TIMEOUT
            };
            try { wrap.dispatchEvent(new CustomEvent('tp:nopicture', { bubbles: true, detail: detail })); } catch(_){}
            if(opts.onNoPicture){ try { opts.onNoPicture(detail, self); } catch(_){} }
            try {
                if(typeof window!=='undefined' && typeof window.__aeFeature==='function'){
                    var _np = { src: 'shared-player', reason: detail.reason, code: detail.code,
                                rs: detail.readyState, ns: detail.networkState };
                    if(opts.telemetryRole) _np.role = opts.telemetryRole;
                    window.__aeFeature('no_picture', _np);
                }
            } catch(_){}
        }, PICTURE_TIMEOUT);
    }
    self.hasPicture = hasPicture;
    /* Re-arm, optionally with a different patience. A caller that KNOWS work
       is still happening (a converter reporting progress, a segment landing)
       uses this so the watchdog measures SILENCE rather than elapsed time —
       measured on prod, the first progressive segment takes 83 s at the 10th
       percentile and 750 s at the slowest, so a fixed deadline would have
       condemned working conversions instead of catching dead ones. */
    self.expectPicture = function(ms){
        if(self._destroyed || sawPicture) return;
        if(typeof ms === 'number') PICTURE_TIMEOUT = ms;
        armPictureWatchdog();
    };

    /* ═══ Public API ═══ */

    self.load = function(srcOrFile){
        if(typeof srcOrFile === 'string'){
            video.src = srcOrFile;
        } else if(srcOrFile instanceof File || srcOrFile instanceof Blob){
            var url = URL.createObjectURL(srcOrFile);
            video.src = url;
            video._blobUrl = url;
        }
        video.load();
        armPictureWatchdog();
    };

    self.play = function(){ video.play().catch(function(){}); };
    self.pause = function(){ video.pause(); };
    self.togglePlay = function(){
        if(video.paused){
            // If at or past trim end, restart from trim start
            if(showTrim && video.currentTime >= trimE - 0.1){
                video.currentTime = trimS;
            }
            // play() returns undefined (not a Promise) on some WebKit/Yandex
            // states — e.g. a video element with no supported source — so a bare
            // .catch threw "Cannot read properties of undefined (reading 'catch')"
            // (TIMBRICA-FRONTEND-BS). Guard per MDN.
            var _pp = video.play();
            if (_pp && _pp.catch) _pp.catch(function(){});
        } else {
            video.pause();
        }
    };

    self.seek = function(t){ video.currentTime = clamp(t, 0, dur || Infinity); updateTime(); };
    self.skip = function(delta){
        video.currentTime = clamp(video.currentTime + delta, showTrim?trimS:0, showTrim?trimE:(dur||Infinity));
        updateTime();
    };

    /* ── A-B repeat ────────────────────────────────────────────────────
       Loop a stretch: the language-learning replay, the "what did he just
       say" replay, and the "watch that stunt again" replay. VLC and MPC-HC
       have had it for years; no browser player does.
       Enforced on timeupdate rather than a timer so it survives seeking,
       speed changes and a stalled network. */
    var abA = null, abB = null;
    function abWatch(){
        if (abA == null || abB == null) return;
        if (video.currentTime >= abB - 0.03 || video.currentTime < abA - 0.5) {
            video.currentTime = abA;
            if (video.paused) { try { video.play(); } catch(_){} }
        }
    }
    video.addEventListener('timeupdate', abWatch);
    self.setLoop = function(a, b){
        if (a == null || b == null) { abA = abB = null; return null; }
        var lo = Math.min(a, b), hi = Math.max(a, b);
        if (hi - lo < 0.25) return null;          // too short to be a loop
        abA = lo; abB = hi;
        if (video.currentTime < lo || video.currentTime > hi) video.currentTime = lo;
        return { start: lo, end: hi };
    };
    self.getLoop = function(){ return (abA == null || abB == null) ? null : { start: abA, end: abB }; };
    self.clearLoop = function(){ abA = abB = null; };

    self.setSpeed = function(r){
        currentSpeed = r;
        applyRate(r);
        if(speedBtn) speedBtn.textContent = r + 'x';
    };

    self.setVolume = function(v){
        video.volume = clamp(v, 0, 1);
        video.muted = v <= 0;
        if(v > 0) currentVolume = v;
        // volumechange event triggers paintVolume automatically when showVolume is on
    };

    self.setTrimRange = function(s, e){
        if(!showTrim) return;
        trimS = clamp(s, 0, dur || Infinity);
        trimE = clamp(e, trimS + 0.1, dur || Infinity);
        updateTrimUI();
        if(opts.onTrimChange) opts.onTrimChange(trimS, trimE);
    };

    self.getTrimRange = function(){ return {start: trimS, end: trimE}; };

    Object.defineProperty(self, 'currentTime', { get: function(){ return video.currentTime; } });
    Object.defineProperty(self, 'duration', { get: function(){ return dur; } });
    Object.defineProperty(self, 'paused', { get: function(){ return video.paused; } });

    self.toggleFullscreen = function(){
        if(fsBtn) fsBtn.click();
    };

    self.destroy = function(){
        self._destroyed = true;   // suppress the teardown 'error' (removeAttribute+load)
        cancelPictureTimer();
        video.pause();
        if(video._blobUrl) URL.revokeObjectURL(video._blobUrl);
        video.removeAttribute('src'); video.load();
        document.removeEventListener('fullscreenchange', onFsChange);
        document.removeEventListener('webkitfullscreenchange', onFsChange);
        if (cssEscHandler) { document.removeEventListener('keydown', cssEscHandler, true); cssEscHandler = null; }
        clearTimeout(controlsTimer);
        wrap.remove();
    };

    self.container = wrap;
}

window.TimbricaPlayer = TimbricaPlayer;
/* The volume control on its own, for play rows that are not a full player —
   /audio-to-video's waveform row is the first. Exported from HERE rather than
   from a file of its own so there is exactly one implementation and one
   stylesheet: a second copy is how the site ended up with 96 horizontal
   sliders that look nothing like the player's. */
window.__createVolumeKnob = createVolumeKnob;

})();
